import { useMemo } from 'react';
import type { ICellRendererParams } from 'ag-grid-community';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { Button, Dropdown } from 'react-bootstrap';
import SubtleBadge from '@/components/common/SubtleBadge';
import type { ProjectJobState } from '../api/types';
import { formatTime } from '../lib/datetime';
import { jobStateLabel, jobStateTone } from '../lib/job-labels';
import { IndexerAgGrid } from './IndexerAgGrid';
import { IndexerIcon } from './IndexerIcon';

export interface ScheduleTableRow {
  id: string;
  name: string;
  enabled: boolean;
  detail: string;
  lastRunAt: string | null;
  lastRunStatus: string | null;
}

export function ScheduleListTable({
  rows,
  detailHeader,
  emptyLabel,
  onCreate,
  onEdit,
  onDelete,
}: {
  rows: ScheduleTableRow[];
  detailHeader: string;
  emptyLabel: string;
  onCreate: () => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  const columns = useMemo(
    () => [
      {
        field: 'name',
        title: 'نام',
        type: 'string',
        flex: 1.2,
        minWidth: 140,
        searchable: true,
        cellRenderer: (params: ICellRendererParams<ScheduleTableRow>) => {
          const row = params.data;
          if (!row) return null;
          return (
            <span className="d-flex align-items-center h-100 fw-medium">
              {row.name}
            </span>
          );
        },
      },
      {
        field: 'enabled',
        title: 'وضعیت',
        type: 'string',
        width: 110,
        cellRenderer: (params: ICellRendererParams<ScheduleTableRow>) => {
          const row = params.data;
          if (!row) return null;
          return (
            <span className="d-flex align-items-center h-100 w-100">
              <SubtleBadge
                bg={row.enabled ? 'success' : 'secondary'}
                pill
                className="fs--2"
                style={{ lineHeight: 1.35 }}
              >
                {row.enabled ? 'فعال' : 'خاموش'}
              </SubtleBadge>
            </span>
          );
        },
      },
      {
        field: 'detail',
        title: detailHeader,
        type: 'string',
        flex: 1.6,
        minWidth: 180,
        searchable: true,
        cellRenderer: (params: ICellRendererParams<ScheduleTableRow>) => {
          const detail = params.data?.detail;
          if (!detail) return <span className="text-secondary">—</span>;
          return (
            <span className="d-flex align-items-center h-100 text-secondary text-truncate" title={detail}>
              {detail}
            </span>
          );
        },
      },
      {
        field: 'lastRunAt',
        title: 'آخرین اجرا',
        type: 'string',
        width: 200,
        minWidth: 160,
        cellRenderer: (params: ICellRendererParams<ScheduleTableRow>) => {
          const row = params.data;
          if (!row) return null;
          if (!row.lastRunAt) {
            return <span className="text-secondary">—</span>;
          }
          const status = row.lastRunStatus as ProjectJobState | null;
          return (
            <span className="d-flex align-items-center h-100 gap-2 text-secondary fs--1">
              {formatTime(row.lastRunAt)}
              {status && (
                <SubtleBadge
                  bg={jobStateTone(status)}
                  pill
                  className="fs--2"
                  style={{ lineHeight: 1.35 }}
                >
                  {jobStateLabel(status)}
                </SubtleBadge>
              )}
            </span>
          );
        },
      },
      {
        field: '_actions',
        title: ' ',
        width: 52,
        sortable: false,
        searchable: false,
        pinned: 'left',
        lockPosition: true,
        suppressMovable: true,
        suppressHeaderMenuButton: true,
        noFilter: true,
        cellStyle: { textAlign: 'center', margin: '0px' },
        cellClass: 'over-flow',
        cellRenderer: (params: ICellRendererParams<ScheduleTableRow>) => {
          const row = params.data;
          if (!row) return null;
          return (
            <div className="w-100 d-flex h-100 align-items-center justify-content-center">
              <span className="allaho_akbar_zIndex">
                <Dropdown
                  onClick={(e) => e.stopPropagation()}
                  align="start"
                  className="allaho_akbar_zIndex overflow-visible btn-reveal-trigger d-inline-block me-0"
                >
                  <Dropdown.Toggle
                    className="text-600 btn-reveal"
                    split
                    variant="reveal"
                    size="sm"
                  >
                    <FontAwesomeIcon icon="ellipsis-h" className="fs--2" />
                  </Dropdown.Toggle>
                  <Dropdown.Menu
                    className="border py-0"
                    popperConfig={{ strategy: 'fixed' }}
                    renderOnMount
                  >
                    <Dropdown.Item
                      onClick={() => onEdit(row.id)}
                      className={classNames(
                        'link-600 fs--1 d-flex align-items-center gap-1 p-0 px-1',
                      )}
                    >
                      <FontAwesomeIcon icon="pen" className="me-1 ms-2" />
                      ویرایش
                    </Dropdown.Item>
                    <Dropdown.Divider className="my-0" />
                    <Dropdown.Item
                      onClick={() => onDelete(row.id)}
                      className="link-600 text-danger fs--1 d-flex align-items-center gap-1 p-0 px-1"
                    >
                      <FontAwesomeIcon
                        icon="trash-alt"
                        className="me-1 fs-0 ms-2"
                      />
                      حذف
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </span>
            </div>
          );
        },
      },
    ],
    [detailHeader, onDelete, onEdit],
  );

  const otherOptions = useMemo(
    () => ({
      getRowId: (params: { data: ScheduleTableRow }) => params.data.id,
      rowHeight: 56,
      animateRows: false,
      suppressCellFocus: true,
    }),
    [],
  );

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <span className="text-secondary small">
          {rows.length > 0
            ? `${rows.length} زمان‌بندی`
            : 'هنوز زمان‌بندی ساخته نشده است.'}
        </span>
        <Button size="sm" variant="falcon-primary" type="button" onClick={onCreate}>
          + زمان‌بندی جدید
        </Button>
      </div>

      {rows.length === 0 ? (
        <div className="d-flex flex-column align-items-center justify-content-center gap-2 text-center text-secondary py-5 border border-dashed rounded-3 bg-body-tertiary">
          <IndexerIcon name="calendar-event" />
          <p className="mb-0">{emptyLabel}</p>
          <Button size="sm" variant="outline-primary" type="button" onClick={onCreate}>
            ساخت اولین زمان‌بندی
          </Button>
        </div>
      ) : (
        <div className="ag-grid-in-card">
          <IndexerAgGrid
            tableName="indexer_schedules"
            columns={columns}
            data={rows}
            otherOptions={otherOptions}
          />
        </div>
      )}
    </>
  );
}
